import { api } from './api';

export interface Producto {
  id: number;
  tenantId: number;
  categoriaId?: number;
  sku?: string;
  name: string;
  description?: string;
  tipo: 'producto' | 'servicio' | 'combo';
  precioBase: number;
  costoPromedio?: number;
  costoEstandar?: number;
  imagenUrl?: string;
  codigoBarras?: string;
  unidadMedida: string;
  manejaInventario: boolean;
  isActive: boolean;
  categoria?: {
    id: number;
    name: string;
  };
  variantes?: Array<{
    id: number;
    name: string;
    precioAdicional: number;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductoCreate {
  name: string;
  sku?: string;
  description?: string;
  tipo?: 'producto' | 'servicio' | 'combo';
  precioBase: number;
  categoriaId?: number;
  codigoBarras?: string;
  unidadMedida?: string;
  manejaInventario?: boolean;
  costoPromedio?: number;
  costoEstandar?: number;
  imagenUrl?: string;
  tenantId?: number;
}

export interface ProductoUpdate {
  name?: string;
  sku?: string;
  description?: string;
  tipo?: 'producto' | 'servicio' | 'combo';
  precioBase?: number;
  categoriaId?: number;
  codigoBarras?: string;
  unidadMedida?: string;
  manejaInventario?: boolean;
  costoPromedio?: number;
  costoEstandar?: number;
  imagenUrl?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  data: {
    [key: string]: T[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const productoService = {
  getAll: async (
    page = 1,
    limit = 10,
    search?: string,
    tipo?: string,
    categoriaId?: number,
    isActive?: boolean
  ): Promise<PaginatedResponse<Producto>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (search) params.append('search', search);
    if (tipo) params.append('tipo', tipo);
    if (categoriaId) params.append('categoriaId', categoriaId.toString());
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    
    const response = await api.get<PaginatedResponse<Producto>>(`/productos?${params}`);
    return response.data;
  },

  getById: async (id: number): Promise<{ data: { producto: Producto } }> => {
    const response = await api.get<{ data: { producto: Producto } }>(`/productos/${id}`);
    return response.data;
  },

  create: async (data: ProductoCreate): Promise<{ data: { producto: Producto } }> => {
    const response = await api.post<{ data: { producto: Producto } }>('/productos', data);
    return response.data;
  },

  update: async (id: number, data: ProductoUpdate): Promise<{ data: { producto: Producto } }> => {
    const response = await api.put<{ data: { producto: Producto } }>(`/productos/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/productos/${id}`);
  },

  exportCsv: async (
    search?: string,
    tipo?: string,
    categoriaId?: number,
    isActive?: boolean
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tipo) params.append('tipo', tipo);
    if (categoriaId) params.append('categoriaId', categoriaId.toString());
    if (isActive !== undefined) params.append('isActive', isActive.toString());

    const response = await api.get(`/productos/export?${params}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  },

  importCsv: async (
    file: File,
    options?: { dryRun?: boolean; createCategories?: boolean }
  ): Promise<{ data: { created: number; updated: number; errors: Array<{ row: number; message: string }> } }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.dryRun !== undefined) formData.append('dryRun', String(options.dryRun));
    if (options?.createCategories !== undefined) formData.append('createCategories', String(options.createCategories));

    const response = await api.post('/productos/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  importCva: async (
    url: string,
    options?: { 
      dryRun?: boolean; 
      createCategories?: boolean; 
      marca?: string; 
      grupo?: string;
      porcentaje?: number;
      incluirPromos?: boolean;
      incluirDescripciones?: boolean;
      incluirSucursales?: boolean;
      incluirDepto?: boolean;
    }
  ): Promise<{ data: { created: number; updated: number; totalProcessed: number; errors: Array<{ row: number; message: string }> } }> => {
    const response = await api.post('/productos/import-cva', {
      url,
      dryRun: options?.dryRun || false,
      createCategories: options?.createCategories || false,
      marca: options?.marca,
      grupo: options?.grupo,
      porcentaje: options?.porcentaje,
      incluirPromos: options?.incluirPromos || false,
      incluirDescripciones: options?.incluirDescripciones || false,
      incluirSucursales: options?.incluirSucursales || false,
      incluirDepto: options?.incluirDepto || false
    });
    return response.data;
  }
};

